import { useAgentComplianceReviewDocumentQuery } from '@client/graphql/__generated__/main-operations';
import { AgentComplianceReviewDocumentQuery } from '@client/graphql/__generated__/types';

type AgentComplianceReviewDocument =
  AgentComplianceReviewDocumentQuery['agentComplianceReviewDocument'];
type AgentComplianceReviewDocumentQueryResult = ReturnType<
  typeof useAgentComplianceReviewDocumentQuery
>;

interface UseAgentComplianceReviewDocumentArgs {
  token: string | undefined;
  ingestionFolderUuid: string | undefined;
  ingestionDocumentId: string | undefined;
}

interface UseAgentComplianceReviewDocumentResult {
  document: AgentComplianceReviewDocument | undefined;
  loading: boolean;
  error: AgentComplianceReviewDocumentQueryResult['error'];
  refetchDocument: AgentComplianceReviewDocumentQueryResult['refetch'];
}

/**
 * Loads the detail (PDF url, fields, suggestions, successful checks) for a
 * single selected document in the agent compliance review.
 *
 * Skips until a document is selected and the token/folder are known. Uses
 * `cache-and-network` so revisiting an already-viewed document is instant while
 * the short-lived PDF URL is refreshed in the background. Opts out of the global
 * error toast (`skipGlobalErrorHandler`) so callers can render inline error
 * state for initial-load failures while preserving cached document data when a
 * background refresh fails.
 */
export const useAgentComplianceReviewDocument = ({
  token,
  ingestionFolderUuid,
  ingestionDocumentId,
}: UseAgentComplianceReviewDocumentArgs): UseAgentComplianceReviewDocumentResult => {
  const { data, loading, error, refetch } =
    useAgentComplianceReviewDocumentQuery({
      variables: {
        input: {
          token: token ?? '',
          ingestionFolderUuid: ingestionFolderUuid ?? '',
          ingestionDocumentId: ingestionDocumentId ?? '',
        },
      },
      skip: !ingestionDocumentId || !token || !ingestionFolderUuid,
      fetchPolicy: 'cache-and-network',
      context: { skipGlobalErrorHandler: true },
    });

  const document = data?.agentComplianceReviewDocument;

  return {
    document,
    loading: loading && !document,
    error: document ? undefined : error,
    refetchDocument: refetch,
  };
};
